/**
 * @voltxampere/web — AC analysis runner store (Sprint 20)
 *
 * Mirrors the transient/DC pattern: signal-driven `currentACResult`
 * + `acRunning` + `acError`. The runner is injected from `main.ts`
 * so tests can supply a fake without spawning an engine worker.
 *
 * AC has no live mode in Sprint 20 — frequency sweeps take long
 * enough (~50-200 ms even for small circuits) that single-shot
 * suffices for the F6 shortcut. A future Sprint may add a "rerun
 * on circuit edit" auto-trigger; for now the user explicitly
 * presses F6 to re-run.
 *
 * Wire shape comes from the engine's `acResultToWire` helper:
 *   meta.acFrequencies: number[]
 *   meta.acNodeMagnitudes: Record<NetId, number[]>
 *   meta.acNodePhases: Record<NetId, number[]>
 *   meta.acBranchMagnitudes: Record<ComponentId, number[]>
 *   meta.acBranchPhases: Record<ComponentId, number[]>
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import type { Circuit } from "@voltxampere/schema";

import { t } from "../i18n/index.js";
import { showToast } from "./toasts.js";
import { gateCircuitForEngine } from "./validation-gate.js";

export interface ACSweepOpts {
  readonly fStart?: number;
  readonly fStop?: number;
  readonly points?: number;
  readonly sweep?: "dec" | "oct" | "lin";
}

export interface ACRunOpts {
  readonly sweep?: ACSweepOpts;
  readonly signal?: AbortSignal;
}

/**
 * Bode trace for one net or one branch. Per-frequency arrays of
 * magnitude (linear) and phase (radians).
 */
export interface ACTrace {
  readonly magnitudes: readonly number[];
  readonly phases: readonly number[];
}

/**
 * Wire-shape AC result the runner exposes to UI consumers. Maps in
 * the engine's wire payload arrive as plain JSON records; we
 * re-bundle them per-net / per-branch with helper accessors.
 */
export interface ACResultLike {
  readonly converged: boolean;
  readonly frequencies: readonly number[];
  readonly nodeTraces: ReadonlyMap<string, ACTrace>;
  readonly branchTraces: ReadonlyMap<string, ACTrace>;
  readonly warnings: readonly string[];
  readonly meta: {
    readonly dcOPElapsedMs: number;
    readonly sweepElapsedMs: number;
    readonly sweepPoints: number;
  };
}

export type ACRunner = (circuit: Circuit, opts?: ACRunOpts) => Promise<ACResultLike>;

let runner: ACRunner | null = null;

export function setACRunner(r: ACRunner | null): void {
  runner = r;
}

export const currentACResult = signal<ACResultLike | null>(null);
export const acRunning = signal<boolean>(false);
export const acError = signal<string | null>(null);

// Sprint 25 R3 lane 3 — concurrent-run guard. Mirrors `runDc` and
// `runTransient` single-shot behaviour: a fast F6×2 aborts the
// in-flight sweep and lets the latest call settle into
// `currentACResult`.
let activeAcAbort: AbortController | null = null;

/**
 * Single-shot AC sweep. Stores the parsed result on
 * `currentACResult` for the waveform AC mode (Sprint 20 commit C5)
 * to consume. `acError` holds the last error string when the run
 * fails (DC OP non-convergence, malformed config, abort, …).
 */
export async function runAC(circuit: Circuit, opts: ACRunOpts = {}): Promise<void> {
  if (!runner) {
    acError.value = "AC runner not configured";
    if (import.meta.env.DEV) {
      console.warn("[VXA F6] runAC called but no runner configured (worker boot failed?)");
    }
    return;
  }
  // Sprint 25 R2 lane 2 — pre-engine validation gate (TS-008).
  if (!gateCircuitForEngine(circuit, { warnings: true })) {
    return;
  }
  // Sprint 25 R3 lane 3 — abort prior in-flight sweep when caller
  // does not pass an explicit signal. External-signal callers (CI,
  // future scheduled sweeps) keep their own abort semantics.
  if (opts.signal === undefined && activeAcAbort !== null) {
    activeAcAbort.abort();
  }
  const ctl = opts.signal === undefined ? new AbortController() : null;
  if (ctl !== null) activeAcAbort = ctl;
  acRunning.value = true;
  acError.value = null;
  if (import.meta.env.DEV) {
    console.info(
      `[VXA F6] runAC start · circuit=${circuit.name} components=${String(circuit.components.length)}`,
    );
  }
  try {
    const result = await runner(circuit, opts);
    if (ctl?.signal.aborted === true) return;
    currentACResult.value = result;
    // Sprint 26 Lane 6 (R5-IA-8) — surface AC sweep completion
    // through the aria-live toast region. Mirrors the DC toast
    // shipped in Lane 2 so screen-reader users are notified
    // when an F6 sweep settles.
    showToast({
      kind: "success",
      title: t("toast.ac.refreshed.title"),
      message: t("toast.ac.refreshed.body"),
    });
    if (import.meta.env.DEV) {
      console.info(
        `[VXA F6] runAC result · converged=${String(result.converged)} points=${String(result.frequencies.length)} nets=${String(result.nodeTraces.size)} sweepMs=${result.meta.sweepElapsedMs.toFixed(1)}`,
      );
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return;
    }
    if (ctl?.signal.aborted === true) return;
    acError.value = err instanceof Error ? err.message : String(err);
    if (import.meta.env.DEV) {
      console.error("[VXA F6] runAC threw:", err);
    }
  } finally {
    if (ctl !== null && activeAcAbort === ctl) {
      activeAcAbort = null;
      acRunning.value = false;
    } else if (ctl === null) {
      acRunning.value = false;
    }
  }
}

/** Test cleanup — wipes the result + state signals. */
export function resetACStore(): void {
  currentACResult.value = null;
  acRunning.value = false;
  acError.value = null;
  if (activeAcAbort !== null) {
    activeAcAbort.abort();
    activeAcAbort = null;
  }
}

/**
 * Helper: parse the engine's wire-shape `AnalysisResult` into the
 * `ACResultLike` shape the store expects. The engine's worker
 * client returns the wire envelope verbatim; main.ts wraps the
 * client call with this helper so the store always sees parsed
 * traces.
 */
export function parseACWire(wire: {
  readonly converged: boolean;
  readonly warnings: readonly string[];
  readonly meta: Record<string, unknown>;
}): ACResultLike {
  const meta = wire.meta;
  const frequencies = Array.isArray(meta.acFrequencies) ? (meta.acFrequencies as number[]) : [];
  const buildTraceMap = (key: string): ReadonlyMap<string, ACTrace> => {
    const magsValue = meta[`${key}Magnitudes`];
    const phasesValue = meta[`${key}Phases`];
    const magsRecord =
      magsValue && typeof magsValue === "object"
        ? (magsValue as Record<string, number[] | undefined>)
        : {};
    const phasesRecord =
      phasesValue && typeof phasesValue === "object"
        ? (phasesValue as Record<string, number[] | undefined>)
        : {};
    const out = new Map<string, ACTrace>();
    for (const id of Object.keys(magsRecord)) {
      out.set(id, {
        magnitudes: magsRecord[id] ?? [],
        phases: phasesRecord[id] ?? [],
      });
    }
    return out;
  };
  const nodeTraces = buildTraceMap("acNode");
  const branchTraces = buildTraceMap("acBranch");
  return {
    converged: wire.converged,
    frequencies,
    nodeTraces,
    branchTraces,
    warnings: [...wire.warnings],
    meta: {
      dcOPElapsedMs: typeof meta.dcOPElapsedMs === "number" ? meta.dcOPElapsedMs : 0,
      sweepElapsedMs: typeof meta.sweepElapsedMs === "number" ? meta.sweepElapsedMs : 0,
      sweepPoints: typeof meta.sweepPoints === "number" ? meta.sweepPoints : frequencies.length,
    },
  };
}
